import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Star, MapPin, CheckCircle, ArrowSquareOut, Heart } from "@phosphor-icons/react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

const Stars = ({ value, onChange, size = 22, readOnly }) => (
  <div className="flex gap-1">
    {[1, 2, 3, 4, 5].map((n) => (
      <button key={n} type="button" disabled={readOnly}
        data-testid={onChange ? `star-${n}` : undefined}
        onClick={() => onChange && onChange(n)}
        className={`transition-transform ${onChange ? "hover:scale-110" : ""} ${readOnly ? "cursor-default" : ""}`}>
        <Star weight={n <= value ? "fill" : "regular"} size={size} className={n <= value ? "text-amber-400" : "text-slate-300"} />
      </button>
    ))}
  </div>
);

export default function PublicReview() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [rating, setRating] = useState(0);
  const [name, setName] = useState("");
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null); // {positive, review_links}

  useEffect(() => {
    api.get(`/public/review/${slug}`).then((r) => setData(r.data)).catch(() => setNotFound(true));
  }, [slug]);

  const submit = async (e) => {
    e.preventDefault();
    if (!rating) { toast.error("Please pick a star rating"); return; }
    if (!name.trim()) { toast.error("Please enter your name"); return; }
    setBusy(true);
    try {
      const { data: res } = await api.post(`/public/review/${slug}`, { reviewer_name: name, rating, comment });
      setResult(res);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  if (notFound) return <Centered><p className="text-slate-600">This review page doesn't exist.</p></Centered>;
  if (!data) return <Centered><div className="h-10 w-10 rounded-full border-2 border-slate-200 border-t-[#0055FF] animate-spin" /></Centered>;

  const b = data.business;

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-xl mx-auto px-4 py-10">
        <div className="flex items-center justify-center mb-8">
          <img src="/citehub-logo.png" alt="Cite-Hub" className="h-10 w-auto" />
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-8">
          <h1 className="font-display text-3xl font-black tracking-tighter text-slate-900">{b.name}</h1>
          {(b.city || b.category) && <p className="text-sm text-slate-500 mt-1">{[b.category, [b.city, b.state].filter(Boolean).join(", ")].filter(Boolean).join(" · ")}</p>}
          {data.review_count > 0 && (
            <div className="flex items-center gap-2 mt-3">
              <Stars value={Math.round(data.average_rating)} readOnly size={18} />
              <span className="text-sm font-semibold text-slate-700">{data.average_rating}</span>
              <span className="text-sm text-slate-400">({data.review_count})</span>
            </div>
          )}

          {result ? (
            <div className="mt-8 text-center" data-testid="review-result">
              {result.positive ? (
                <>
                  <CheckCircle weight="fill" size={56} className="text-emerald-500 mx-auto" />
                  <h2 className="font-display text-2xl font-bold text-slate-900 mt-4">Thank you! 🎉</h2>
                  <p className="text-slate-600 mt-2">We'd love if you shared it publicly — it only takes a moment:</p>
                  <div className="mt-5 space-y-2.5">
                    {(result.review_links || []).length === 0 && (
                      <p className="text-sm text-slate-400">Your review has been posted to our profile.</p>
                    )}
                    {(result.review_links || []).map((l, i) => (
                      <a key={i} href={l.url} target="_blank" rel="noopener noreferrer" data-testid={`post-link-${i}`}
                        className="flex items-center justify-center gap-2 rounded-md bg-[#0055FF] hover:bg-[#0044CC] text-white font-semibold py-3 transition-colors">
                        Post on {l.label} <ArrowSquareOut size={17} />
                      </a>
                    ))}
                  </div>
                </>
              ) : (
                <>
                  <Heart weight="fill" size={56} className="text-[#0055FF] mx-auto" />
                  <h2 className="font-display text-2xl font-bold text-slate-900 mt-4">Thank you for the feedback</h2>
                  <p className="text-slate-600 mt-2">We're sorry we fell short. The owner has received your comments privately and will make it right.</p>
                </>
              )}
            </div>
          ) : (
            <form onSubmit={submit} className="mt-8 space-y-5">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.1em] text-slate-500 mb-2">Your rating</p>
                <Stars value={rating} onChange={setRating} size={34} />
              </div>
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.1em] text-slate-500 mb-2">Your name</p>
                <Input data-testid="review-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Jane D." />
              </div>
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.1em] text-slate-500 mb-2">Your review</p>
                <Textarea data-testid="review-comment-input" value={comment} onChange={(e) => setComment(e.target.value)} rows={4} placeholder="Tell us about your experience…" />
              </div>
              <Button type="submit" disabled={busy} data-testid="review-submit-button"
                className="w-full h-12 bg-[#0055FF] hover:bg-[#0044CC] text-white font-semibold">
                {busy ? "Submitting…" : "Submit review"}
              </Button>
            </form>
          )}
        </div>

        {data.reviews.length > 0 && !result && (
          <div className="mt-6 space-y-3">
            <p className="text-xs font-bold uppercase tracking-[0.1em] text-slate-500 px-1">Recent reviews</p>
            {data.reviews.slice(0, 8).map((r, i) => (
              <div key={i} className="rounded-xl border border-slate-200 bg-white p-4">
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-slate-900">{r.reviewer_name}</span>
                  <Stars value={r.rating} readOnly size={14} />
                </div>
                {r.comment && <p className="text-sm text-slate-600 mt-1.5">{r.comment}</p>}
                {r.response && <p className="text-sm text-slate-700 mt-2 pl-3 border-l-2 border-[#0055FF]"><span className="font-semibold">Owner: </span>{r.response}</p>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

const Centered = ({ children }) => (
  <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">{children}</div>
);
